import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Unsubscribe,
} from '@firebase/firestore';
import { db } from './firebase';
import type { Place, PlaceCreateInput, PlaceFilter, PlaceStatus, PlaceVisibility } from '../types';

const COLLECTION_NAME = 'places';

/**
 * Create a new place
 * - Private places: status 'approved' (immediately visible to creator)
 * - Public places: status 'pending' (needs moderation)
 */
export async function createPlace(
  placeData: PlaceCreateInput,
  createdBy: string
): Promise<string> {
  const visibility = placeData.visibility || 'public';
  // Private places are immediately approved (visible to creator)
  // Public places go through moderation
  const status: PlaceStatus = visibility === 'private' ? 'approved' : 'pending';

  const docRef = await addDoc(collection(db, COLLECTION_NAME), {
    ...placeData,
    createdBy,
    visibility,
    status,
    rating: 0,
    reviewCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Get a place by ID
 */
export async function getPlaceById(id: string): Promise<Place | null> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Place;
}

/**
 * Get all public approved places (for main view)
 */
export async function getApprovedPlaces(): Promise<Place[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('status', '==', 'approved'),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  const places = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Place[];

  // Filter to only public places (or places without visibility for backwards compatibility)
  return places.filter((place) => !place.visibility || place.visibility === 'public');
}

/**
 * Get all places visible to a specific user
 * - Public approved places
 * - User's own places (all statuses)
 * - Places shared with the user
 */
export async function getVisiblePlaces(userId: string | null): Promise<Place[]> {
  // Get all approved places
  const q = query(
    collection(db, COLLECTION_NAME),
    where('status', '==', 'approved'),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  const places = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Place[];

  // Filter places visible to the user
  return places.filter((place) => {
    // Public places (or places without visibility for backwards compatibility)
    if (!place.visibility || place.visibility === 'public') {
      return true;
    }

    // Private places: only visible to creator or shared users
    if (place.visibility === 'private' && userId) {
      // User is the creator
      if (place.createdBy === userId) {
        return true;
      }
      // User is in sharedWith list
      if (place.sharedWith?.includes(userId)) {
        return true;
      }
    }

    return false;
  });
}

/**
 * Get places shared with a specific user
 */
export async function getSharedPlaces(userId: string): Promise<Place[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('sharedWith', 'array-contains', userId),
    where('status', '==', 'approved')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Place[];
}

/**
 * Get all places (for admin/moderation)
 */
export async function getAllPlaces(): Promise<Place[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Place[];
}

/**
 * Get places created by a specific user
 */
export async function getUserPlaces(userId: string): Promise<Place[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('createdBy', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Place[];
}

/**
 * Search and filter places
 */
export async function searchPlaces(filters: PlaceFilter): Promise<Place[]> {
  // Start with approved places
  let q = query(
    collection(db, COLLECTION_NAME),
    where('status', '==', 'approved')
  );

  // Add category filter if specified
  if (filters.category) {
    q = query(q, where('category', '==', filters.category));
  }

  const snapshot = await getDocs(q);
  let places = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Place[];

  // Client-side filtering for complex queries
  if (filters.searchWhat) {
    const searchLower = filters.searchWhat.toLowerCase();
    places = places.filter((place) => {
      const matchesName = place.name.toLowerCase().includes(searchLower);
      const matchesTagline = place.tagline.toLowerCase().includes(searchLower);
      const matchesMoods = place.moods.some((m) =>
        m.toLowerCase().includes(searchLower)
      );
      const matchesActivities = place.activities.some((a) =>
        a.toLowerCase().includes(searchLower)
      );
      return matchesName || matchesTagline || matchesMoods || matchesActivities;
    });
  }

  if (filters.searchWhere && filters.searchWhere !== 'Aktueller Standort') {
    const searchLower = filters.searchWhere.toLowerCase();
    places = places.filter((place) => {
      const matchesAddress = place.address?.toLowerCase().includes(searchLower);
      const matchesCity = place.city?.toLowerCase().includes(searchLower);
      const matchesPostalCode = place.postalCode?.toLowerCase().includes(searchLower);
      return matchesAddress || matchesCity || matchesPostalCode;
    });
  }

  if (filters.moods.length > 0) {
    places = places.filter((place) =>
      filters.moods.some((mood) => place.moods.includes(mood))
    );
  }

  if (filters.activities.length > 0) {
    places = places.filter((place) =>
      filters.activities.some((activity) => place.activities.includes(activity))
    );
  }

  if (filters.community.length > 0) {
    places = places.filter((place) =>
      filters.community.some((feature) => place.community.includes(feature))
    );
  }

  if (filters.minRating) {
    places = places.filter((place) => place.rating >= filters.minRating!);
  }

  return places;
}

/**
 * Update a place
 */
export async function updatePlace(
  id: string,
  updates: Partial<Place>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);

  // Filter out undefined values (Firestore doesn't accept undefined)
  const cleanedUpdates: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined) {
      cleanedUpdates[key] = value;
    }
  }

  await updateDoc(docRef, {
    ...cleanedUpdates,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Update place rating (called after a review is added/updated)
 */
export async function updatePlaceRating(
  placeId: string,
  newRating: number,
  newReviewCount: number
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, placeId);
  await updateDoc(docRef, {
    rating: newRating,
    reviewCount: newReviewCount,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Subscribe to visible places (real-time)
 * @param callback - Function to call with places
 * @param userId - Optional user ID to include private/shared places
 * @param onError - Optional error callback
 */
export function subscribeToPlaces(
  callback: (places: Place[]) => void,
  userId?: string | null,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('status', '==', 'approved'),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const allPlaces = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Place[];

      // Filter to visible places
      const places = allPlaces.filter((place) => {
        // Public places (or places without visibility for backwards compatibility)
        if (!place.visibility || place.visibility === 'public') {
          return true;
        }

        // Private places: only visible to creator or shared users
        if (place.visibility === 'private' && userId) {
          if (place.createdBy === userId) return true;
          if (place.sharedWith?.includes(userId)) return true;
        }

        return false;
      });

      callback(places);
    },
    (error) => {
      console.error('[PlacesService] subscribeToPlaces error:', error);
      if (onError) {
        onError(error);
      }
      // Return empty array on permission error
      callback([]);
    }
  );
}

/**
 * Subscribe to a single place (real-time)
 */
export function subscribeToPlace(
  placeId: string,
  callback: (place: Place | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const docRef = doc(db, COLLECTION_NAME, placeId);

  return onSnapshot(
    docRef,
    (docSnap) => {
      if (!docSnap.exists()) {
        callback(null);
        return;
      }
      callback({
        id: docSnap.id,
        ...docSnap.data(),
      } as Place);
    },
    (error) => {
      console.error('[PlacesService] subscribeToPlace error:', error);
      if (onError) {
        onError(error);
      }
      callback(null);
    }
  );
}

/**
 * Get popular places (sorted by rating and review count)
 */
export async function getPopularPlaces(limit: number = 10): Promise<Place[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('status', '==', 'approved'),
    orderBy('rating', 'desc')
  );

  const snapshot = await getDocs(q);
  const places = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Place[];

  // Sort by rating first, then by review count, and limit
  return places
    .sort((a, b) => {
      if (b.rating !== a.rating) return b.rating - a.rating;
      return b.reviewCount - a.reviewCount;
    })
    .slice(0, limit);
}

/**
 * Get newest places (recently approved)
 */
export async function getNewPlaces(limit: number = 10): Promise<Place[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('status', '==', 'approved'),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  const places = snapshot.docs
    .slice(0, limit)
    .map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Place[];

  return places;
}

/**
 * Get places by IDs (for favorites-based recommendations)
 */
export async function getPlacesByIds(ids: string[]): Promise<Place[]> {
  if (ids.length === 0) return [];

  const places: Place[] = [];
  for (const id of ids) {
    const place = await getPlaceById(id);
    if (place && place.status === 'approved') {
      places.push(place);
    }
  }
  return places;
}

/**
 * Get recommended places based on user's favorite places' characteristics
 */
export async function getRecommendedPlaces(
  favoritePlaces: Place[],
  excludeIds: string[],
  limit: number = 10
): Promise<Place[]> {
  if (favoritePlaces.length === 0) {
    // If no favorites, return popular places
    return getPopularPlaces(limit);
  }

  // Extract characteristics from favorites
  const favoriteMoods = new Set<string>();
  const favoriteActivities = new Set<string>();
  const favoriteCategories = new Set<string>();

  favoritePlaces.forEach((place) => {
    place.moods.forEach((mood) => favoriteMoods.add(mood));
    place.activities.forEach((activity) => favoriteActivities.add(activity));
    if (place.category) favoriteCategories.add(place.category);
  });

  // Get all approved places
  const allPlaces = await getApprovedPlaces();

  // Score each place based on similarity to favorites
  const scoredPlaces = allPlaces
    .filter((place) => !excludeIds.includes(place.id))
    .map((place) => {
      let score = 0;

      // Category match
      if (place.category && favoriteCategories.has(place.category)) score += 3;

      // Mood matches
      place.moods.forEach((mood) => {
        if (favoriteMoods.has(mood)) score += 2;
      });

      // Activity matches
      place.activities.forEach((activity) => {
        if (favoriteActivities.has(activity)) score += 1;
      });

      // Bonus for higher-rated places
      score += place.rating * 0.5;

      return { place, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ place }) => place);

  return scoredPlaces;
}

/**
 * Delete a place (admin only)
 */
export async function deletePlace(placeId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, placeId);
  await deleteDoc(docRef);
}

/**
 * Make a private place public (triggers moderation)
 * Only the creator can make a place public
 */
export async function makePlacePublic(placeId: string, userId: string): Promise<void> {
  const place = await getPlaceById(placeId);

  if (!place) {
    throw new Error('Ort nicht gefunden');
  }

  if (place.createdBy !== userId) {
    throw new Error('Nur der Ersteller kann einen Ort oeffentlich machen');
  }

  if (place.visibility === 'public') {
    throw new Error('Ort ist bereits oeffentlich');
  }

  const docRef = doc(db, COLLECTION_NAME, placeId);
  await updateDoc(docRef, {
    visibility: 'public' as PlaceVisibility,
    status: 'pending' as PlaceStatus,  // Goes through moderation
    updatedAt: serverTimestamp(),
  });
}

/**
 * Share a place with another user
 */
export async function sharePlaceWithUser(
  placeId: string,
  ownerUserId: string,
  targetUserId: string
): Promise<void> {
  const place = await getPlaceById(placeId);

  if (!place) {
    throw new Error('Ort nicht gefunden');
  }

  if (place.createdBy !== ownerUserId) {
    throw new Error('Nur der Ersteller kann einen Ort teilen');
  }

  if (place.visibility !== 'private') {
    throw new Error('Nur private Orte koennen geteilt werden');
  }

  const sharedWith = place.sharedWith || [];
  if (sharedWith.includes(targetUserId)) {
    return; // Already shared
  }

  const docRef = doc(db, COLLECTION_NAME, placeId);
  await updateDoc(docRef, {
    sharedWith: [...sharedWith, targetUserId],
    updatedAt: serverTimestamp(),
  });
}

/**
 * Remove sharing for a user
 */
export async function unsharePlaceWithUser(
  placeId: string,
  ownerUserId: string,
  targetUserId: string
): Promise<void> {
  const place = await getPlaceById(placeId);

  if (!place) {
    throw new Error('Ort nicht gefunden');
  }

  if (place.createdBy !== ownerUserId) {
    throw new Error('Nur der Ersteller kann Freigaben entziehen');
  }

  const sharedWith = place.sharedWith || [];
  const docRef = doc(db, COLLECTION_NAME, placeId);
  await updateDoc(docRef, {
    sharedWith: sharedWith.filter((id) => id !== targetUserId),
    updatedAt: serverTimestamp(),
  });
}
